import { useEffect, useState } from 'react';
import {
  CheckCircle2,
  Copy,
  FileJson,
  GitBranch,
  Map,
  Loader2,
  Play,
  RotateCcw,
  ScrollText,
  XCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ConceptTreeV3Panel } from '@/components/learningTutorial/ConceptTreeV3Panel';
import { CourseStructureV3Panel } from '@/components/learningTutorial/CourseStructureV3Panel';
import { ArtifactEdmV3Panel } from '@/components/learningTutorial/ArtifactEdmV3Panel';
import { ArtifactStudioV3Panel } from '@/components/learningTutorial/ArtifactStudioV3Panel';
import { ArtifactLayoutEngineV3Panel } from '@/components/learningTutorial/ArtifactLayoutEngineV3Panel';
import { ArtifactSvgRendererV3Panel } from '@/components/learningTutorial/ArtifactSvgRendererV3Panel';
import { ArtifactRraoV3Panel } from '@/components/learningTutorial/ArtifactRraoV3Panel';
import { ArtifactAuthoringV3Panel } from '@/components/learningTutorial/ArtifactAuthoringV3Panel';
import { ArtifactRenderingV3Panel } from '@/components/learningTutorial/ArtifactRenderingV3Panel';
import { ArtifactGenerationV3Panel } from '@/components/learningTutorial/ArtifactGenerationV3Panel';
import { ArtifactSpecificationV3Panel } from '@/components/learningTutorial/ArtifactSpecificationV3Panel';
import { CurriculumOptimizationV3Panel } from '@/components/learningTutorial/CurriculumOptimizationV3Panel';
import { KnowledgeAnalysisV3Panel } from '@/components/learningTutorial/KnowledgeAnalysisV3Panel';
import { SlideArchitectureV3Panel } from '@/components/learningTutorial/SlideArchitectureV3Panel';
import { Flow3ArtifactPipelineMap } from '@/components/learningTutorial/Flow3ArtifactPipelineMap';
import {
  FLOW3_PIPELINE_STEPS,
  flow3StepApiEndpoint,
  flow3StepData,
  flow3StepStatus,
} from '@/lib/learningTutorial/flow3PipelineSteps';
import {
  flow3CanRunStep,
  flow3StepComplete,
  flow3StepMeta,
  flow3StepsAfter,
} from '@/lib/learningTutorial/flow3PipelineRunner';

function formatJson(value) {
  if (value == null) return 'null';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function StatusIcon({ state }) {
  if (state === 'done') return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
  if (state === 'active') return <Loader2 className="w-4 h-4 text-amber-400 animate-spin shrink-0" />;
  if (state === 'ready') return <GitBranch className="w-4 h-4 text-violet-400 shrink-0" />;
  if (state === 'error') return <XCircle className="w-4 h-4 text-red-400 shrink-0" />;
  return <GitBranch className="w-4 h-4 text-slate-600 shrink-0" />;
}

function StepApiJsonPanel({ step, data, projectId, onOpenFlowMap }) {
  const endpoint = flow3StepApiEndpoint(step.id, projectId);
  const [copied, setCopied] = useState(false);
  const jsonText = formatJson(data);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!data) {
    return (
      <div className="rounded-lg border border-slate-700/60 bg-slate-950/50 p-4">
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">API response JSON</p>
        <p className="text-sm text-slate-500">No response yet — run this step to populate the API payload.</p>
        {endpoint ? (
          <p className="text-[11px] font-mono text-violet-300/70 mt-2">
            {endpoint.method} {endpoint.pathTemplate || endpoint.path}
          </p>
        ) : null}
        {onOpenFlowMap ? (
          <p className="text-[11px] text-slate-500 mt-2">
            See{' '}
            <button type="button" className="text-violet-300 hover:text-violet-200 underline" onClick={onOpenFlowMap}>
              Flow map
            </button>
            {' '}
            tab for what this step does to each artifact type.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-emerald-500/25 bg-slate-950/60 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-emerald-500/15 bg-emerald-950/20">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-emerald-400/90">API response JSON</p>
          {endpoint ? (
            <p className="text-[11px] font-mono text-emerald-200/80 truncate" title={endpoint.path}>
              <span className="text-violet-300">{endpoint.method}</span>
              {' '}
              {endpoint.path}
            </p>
          ) : null}
          {onOpenFlowMap ? (
            <p className="text-[11px] text-slate-500 mt-1">
              See{' '}
              <button type="button" className="text-violet-300 hover:text-violet-200 underline" onClick={onOpenFlowMap}>
                Flow map
              </button>
              {' '}
              tab for what this step does to each artifact type.
            </p>
          ) : null}
        </div>
        <Button type="button" size="sm" variant="ghost" className="h-7 text-xs shrink-0" onClick={handleCopy}>
          <Copy className="w-3 h-3 mr-1" />
          {copied ? 'Copied' : 'Copy JSON'}
        </Button>
      </div>
      <pre className="text-xs leading-relaxed text-emerald-100/90 font-mono whitespace-pre-wrap overflow-auto max-h-[40vh] p-3">
        {jsonText}
      </pre>
    </div>
  );
}

function StepOutlineContent({ step, data, pipelineSteps, onArtifactSelect }) {
  if (!data) {
    return <p className="text-sm text-slate-500">Waiting for {step.shortLabel}…</p>;
  }

  if (step.id === 'concept_tree_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <ConceptTreeV3Panel data={data} />
      </div>
    );
  }

  if (step.id === 'knowledge_analysis_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <KnowledgeAnalysisV3Panel data={data} pipelineSteps={pipelineSteps} />
      </div>
    );
  }

  if (step.id === 'course_structure_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <CourseStructureV3Panel data={data} />
      </div>
    );
  }

  if (step.id === 'slide_architecture_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <SlideArchitectureV3Panel data={data} />
      </div>
    );
  }

  if (step.id === 'curriculum_optimization_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <CurriculumOptimizationV3Panel data={data} />
      </div>
    );
  }

  if (step.id === 'artifact_specification_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <ArtifactSpecificationV3Panel data={data} />
      </div>
    );
  }

  if (step.id === 'artifact_generation_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <ArtifactGenerationV3Panel data={data} />
      </div>
    );
  }

  if (step.id === 'artifact_rendering_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <ArtifactRenderingV3Panel data={data} />
      </div>
    );
  }

  if (step.id === 'artifact_edm_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <ArtifactEdmV3Panel data={data} />
      </div>
    );
  }

  if (step.id === 'artifact_authoring_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <ArtifactAuthoringV3Panel data={data} />
      </div>
    );
  }

  if (step.id === 'artifact_studio_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <ArtifactStudioV3Panel data={data} />
      </div>
    );
  }

  if (step.id === 'artifact_layout_engine_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <ArtifactLayoutEngineV3Panel data={data} />
      </div>
    );
  }

  if (step.id === 'artifact_svg_renderer_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <ArtifactSvgRendererV3Panel data={data} onArtifactSelect={onArtifactSelect} />
      </div>
    );
  }

  if (step.id === 'artifact_rrao_v3') {
    return (
      <div className="space-y-3">
        <PromptPartsBanner step={step} />
        <ArtifactRraoV3Panel data={data} />
      </div>
    );
  }

  return null;
}

function StepOutputPanel({
  step,
  data,
  viewMode,
  pipelineSteps,
  projectId,
  onOpenFlowMap,
  onArtifactSelect,
  highlightedStepId,
  highlightedArtifactId,
  onFlowMapStepSelect,
}) {
  if (viewMode === 'flowmap') {
    return (
      <Flow3ArtifactPipelineMap
        highlightedStepId={highlightedStepId}
        highlightedArtifactId={highlightedArtifactId}
        onStepSelect={onFlowMapStepSelect}
      />
    );
  }

  if (viewMode === 'json') {
    return (
      <StepApiJsonPanel
        step={step}
        data={data}
        projectId={projectId}
        onOpenFlowMap={onOpenFlowMap}
      />
    );
  }

  if (viewMode === 'outline') {
    return (
      <StepOutlineContent
        step={step}
        data={data}
        pipelineSteps={pipelineSteps}
        onArtifactSelect={onArtifactSelect}
      />
    );
  }

  return (
    <div className="space-y-4">
      <StepOutlineContent
        step={step}
        data={data}
        pipelineSteps={pipelineSteps}
        onArtifactSelect={onArtifactSelect}
      />
      <StepApiJsonPanel
        step={step}
        data={data}
        projectId={projectId}
        onOpenFlowMap={onOpenFlowMap}
      />
    </div>
  );
}

function PromptPartsBanner({ step }) {
  const parts = step.promptParts || [];
  if (!parts.length) return null;
  return (
    <div className="rounded-lg border border-amber-500/20 bg-amber-950/15 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-amber-400/90 mb-1.5">
        Prompt elements for this step
      </p>
      <div className="flex flex-wrap gap-1">
        {parts.map((p) => (
          <span
            key={p}
            className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/25 bg-slate-900/60 text-amber-200/80"
          >
            {p}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Flow3PipelineOutlineDialog({
  open,
  onOpenChange,
  conceptTree = null,
  knowledgeAnalysis = null,
  courseStructure = null,
  slideArchitecture = null,
  curriculumOptimization = null,
  artifactSpecification = null,
  artifactGeneration = null,
  artifactRendering = null,
  artifactEdm = null,
  artifactAuthoring = null,
  artifactStudio = null,
  artifactLayoutEngine = null,
  artifactSvgRenderer = null,
  artifactRrao = null,
  pipelineStatus = 'idle',
  currentStepId = null,
  initialStepId = null,
  pipelineError = null,
  tutorialName = '',
  topic = '',
  pipelineLoading = false,
  projectId = null,
  onRunStep,
  onRunFromStep,
}) {
  const [selectedStepId, setSelectedStepId] = useState('concept_tree_v3');
  const [viewMode, setViewMode] = useState('both');
  const [highlightedArtifactId, setHighlightedArtifactId] = useState(null);
  const steps = FLOW3_PIPELINE_STEPS;
  const selectedStep = steps.find((s) => s.id === selectedStepId) || steps[0];

  const stepCtx = {
    conceptTree,
    knowledgeAnalysis,
    courseStructure,
    slideArchitecture,
    curriculumOptimization,
    artifactSpecification,
    artifactGeneration,
    artifactRendering,
    artifactEdm,
    artifactAuthoring,
    artifactStudio,
    artifactLayoutEngine,
    artifactSvgRenderer,
    artifactRrao,
    pipelineStatus,
    currentStepId,
  };

  const runCtx = {
    tutorialName,
    topic,
    conceptTree,
    knowledgeAnalysis,
    courseStructure,
    slideArchitecture,
    curriculumOptimization,
    artifactSpecification,
    artifactGeneration,
    artifactRendering,
    artifactEdm,
    artifactAuthoring,
    artifactStudio,
    artifactLayoutEngine,
    artifactSvgRenderer,
    artifactRrao,
    pipelineLoading,
  };

  useEffect(() => {
    if (!open) return;
    const target = initialStepId || currentStepId || 'concept_tree_v3';
    if (steps.some((s) => s.id === target)) {
      setSelectedStepId(target);
    }
  }, [open, currentStepId, initialStepId, steps]);

  useEffect(() => {
    if (!open || pipelineStatus !== 'working' || !currentStepId) return;
    setSelectedStepId(currentStepId);
  }, [open, pipelineStatus, currentStepId]);

  const selectedData = flow3StepData(selectedStep.id, {
    conceptTree,
    knowledgeAnalysis,
    courseStructure,
    slideArchitecture,
    curriculumOptimization,
    artifactSpecification,
    artifactGeneration,
    artifactRendering,
    artifactEdm,
    artifactAuthoring,
    artifactStudio,
    artifactLayoutEngine,
    artifactSvgRenderer,
    artifactRrao,
  });

  const canRun = flow3CanRunStep(selectedStep.id, runCtx);
  const stepDone = flow3StepComplete(selectedStep.id, {
    conceptTree,
    knowledgeAnalysis,
    courseStructure,
    slideArchitecture,
    curriculumOptimization,
    artifactSpecification,
    artifactGeneration,
    artifactRendering,
    artifactEdm,
    artifactAuthoring,
    artifactStudio,
    artifactLayoutEngine,
    artifactSvgRenderer,
    artifactRrao,
  });
  const hasFollowing = flow3StepsAfter(selectedStep.id).length > 0;
  const showRunFooter = Boolean(onRunStep || onRunFromStep);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[88vh] flex flex-col bg-black border border-violet-500/30 text-slate-100 p-0 gap-0"
        data-testid="flow3-pipeline-outline-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-violet-500/20 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ScrollText className="w-5 h-5 text-violet-400" />
            Tutorial Flow 3 — pipeline outline
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Run each step individually or continue from any step. Select a step to inspect its output.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 flex-col md:flex-row">
          <aside className="md:w-60 shrink-0 border-b md:border-b-0 md:border-r border-violet-500/15 bg-slate-950/80 overflow-y-auto">
            <ol className="p-2 space-y-0.5">
              {steps.map((step) => {
                const state = flow3StepStatus(step.id, stepCtx);
                const selected = step.id === selectedStepId;
                return (
                  <li key={step.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedStepId(step.id)}
                      className={cn(
                        'w-full flex items-start gap-2 rounded-md px-2.5 py-2 text-left text-xs transition-colors',
                        selected ? 'bg-violet-600/25 text-violet-100' : 'text-slate-400 hover:bg-slate-800/60',
                      )}
                      data-testid={`flow3-pipeline-step-${step.id}`}
                    >
                      <StatusIcon state={state} />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{step.shortLabel}</p>
                        <p className="text-[10px] text-slate-500 line-clamp-2">{step.description}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>

          <div className="flex flex-col flex-1 min-h-0 min-w-0">
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-violet-500/10 shrink-0">
              <div className="min-w-0">
                <p className="text-sm font-medium text-violet-100 truncate">{selectedStep.label}</p>
                <p className="text-[10px] text-slate-500 truncate">{selectedStep.description}</p>
                {stepDone ? (
                  <p className="text-[10px] text-emerald-400/80 mt-0.5">Step complete — re-run to regenerate</p>
                ) : null}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  type="button"
                  size="sm"
                  variant={viewMode === 'outline' ? 'default' : 'outline'}
                  className="text-xs h-7"
                  onClick={() => setViewMode('outline')}
                >
                  Outline
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={viewMode === 'both' ? 'default' : 'outline'}
                  className="text-xs h-7"
                  onClick={() => setViewMode('both')}
                >
                  Both
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={viewMode === 'json' ? 'default' : 'outline'}
                  className="text-xs h-7"
                  onClick={() => setViewMode('json')}
                >
                  <FileJson className="w-3 h-3 mr-1" />
                  JSON
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={viewMode === 'flowmap' ? 'default' : 'outline'}
                  className="text-xs h-7"
                  onClick={() => setViewMode('flowmap')}
                  data-testid="flow3-view-flowmap"
                >
                  <Map className="w-3 h-3 mr-1" />
                  Flow map
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {pipelineError ? (
                <p className="text-sm text-red-400 mb-3">{pipelineError}</p>
              ) : null}
              <StepOutputPanel
                step={selectedStep}
                data={selectedData}
                viewMode={viewMode}
                pipelineSteps={steps}
                projectId={projectId}
                onOpenFlowMap={() => setViewMode('flowmap')}
                onArtifactSelect={setHighlightedArtifactId}
                highlightedStepId={selectedStepId}
                highlightedArtifactId={
                  selectedStepId === 'artifact_svg_renderer_v3' ? highlightedArtifactId : null
                }
                onFlowMapStepSelect={setSelectedStepId}
              />
            </div>

            {showRunFooter ? (
              <div className="shrink-0 border-t border-violet-500/15 px-4 py-3 space-y-2">
                {!canRun.ok ? (
                  <p className="text-xs text-amber-300/80">{canRun.reason}</p>
                ) : null}
                <div className="flex flex-wrap gap-2 justify-end">
                  {onRunStep ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={!canRun.ok || pipelineLoading}
                      className="border-violet-500/40 text-violet-200"
                      onClick={() => onRunStep(selectedStep.id)}
                      data-testid={`flow3-run-step-${selectedStep.id}`}
                    >
                      {pipelineLoading && currentStepId === selectedStep.id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : stepDone ? (
                        <RotateCcw className="w-4 h-4 mr-2" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      {stepDone ? 'Re-run' : 'Run'} {flow3StepMeta(selectedStep.id).shortLabel}
                    </Button>
                  ) : null}
                  {onRunFromStep && hasFollowing ? (
                    <Button
                      type="button"
                      size="sm"
                      disabled={!canRun.ok || pipelineLoading}
                      className="bg-violet-600 hover:bg-violet-500"
                      onClick={() => onRunFromStep(selectedStep.id)}
                      data-testid={`flow3-run-from-${selectedStep.id}`}
                    >
                      {pipelineLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Run from here
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
